import { History, Location } from "history";
import { ApiService, downloadFile, getFilenameFromHeaders } from "@/api";
import UserStore from "./UserStore";
import ShareholdersStore from "./ShareholdersStore";
import AlertMessageStore from "./AlertMessageStore";
import { AxiosInstance } from "axios";
import { DialogStore, DialogType } from "@/dialogs/dialogDomain";
import PortfolioStore, { getCompaniesWithAdminAccessType } from "./PortfolioStore";
import { DownloadService } from "@/api/DownloadService";
import { AffiliateService } from "@/affiliate/Affiliate";
import { initUserPrefs, UserPrefs } from "./userPrefsDomain";
import { WebSocketStore } from "@/webSocketDomain";
import { makeAutoObservable, observable } from "mobx";
import { SignDocumentPageState } from "@/signing/signDocumentDomain";
import { AcceptUserTermsState } from "@/termOfService/acceptUserTermsDomain";
import { CompanyIds, toAppPath } from "@/routes/companyByIdDomain";
import {
  createEventDispatcher,
  DCEvent,
  handleClearTimeoutEvent,
  handleCopyToClipboardEvent,
  handleHttpEvent,
  handleLoadUrlEvent,
  handleLogErrorEvent,
  handleNavigateEvent,
  handleNavigateToEvent,
  handleReloadEvent,
  handleSetTimeoutEvent,
  handleShowAlertEvent,
  handleThunkEvent,
  handleUpdateStoreEvent,
  OrderLiveGeneralMeetingEvent,
  Timeouts,
} from "@/events/eventsDomain";
import { I18nState, initialI18nState, TranslationAdminState } from "@/i18n/i18nDomain";
import {
  FollowUpCounts,
  PortfolioPageState,
  PortfolioSignaturesState,
} from "@/portfolio/portfolioDomain";
import { ScheduleState } from "@/schedule/scheduleDomain";
import { loadProject } from "@/dbot/project/ProjectInitializerController";
import { AccessType } from "@/types/enums/AccessType";
import { FollowUpsState } from "@/backoffice/backofficeDomain";
import {
  BeneficialOwnersApiClient,
  BeneficialOwnersStore,
} from "@/shareholderRegister/beneficialOwners/beneficialOwnersDomain";
import { CompanySettingsState } from "@/overview/companySettings/companySettingsDomain";
import { ShareTypesState } from "@/shareholderRegister/shareClasses/shareClassesDomain";
import { RootStoreData } from "@/events/rootStoreData";
import { TransactionStore } from "./TransactionStore";
import { MatchedDCRoute } from "@/routes/routes";
import { logDebug } from "@/utils/log";
import { AdminSearchPageState } from "@/search/adminSearchDomain";
import { PortfolioSearchState } from "@/portfolio/search/portfolioSearchDomain";
import { PartnershipRegisterState } from "@/partnershipRegister/partnershipRegisterDomain";
import { EntityDataState } from "@/customFields/customFieldsDomain";
import { AddCompanyState } from "@/addCompany/addCompanyDomain";
import { CompanyImportState } from "@/companyImport/companyImportDomain";
import { OwnershipImportState } from "@/ownershipImport/ownershipImportDomain";
import { FieldHistoryImportState } from "@/customFields/fieldHistoryImportDomain";
import { AccountFieldsState } from "@/customFields/accountFieldsDomain";
import { AccountCompanyRolesState } from "@/companyRoles/accountRolesDomain";
import { DashboardSettingsState } from "@/dashboards/dashboardSettingsDomain";
import { CompanyDashboardsState } from "@/dashboards/companyDashboardsDomain";
import { EntityDataImportState } from "@/customFields/entityDataImportDomain";
import type {
  CompanyFieldDetailsState,
  CompanyFieldEditState,
} from "@/companyField/companyFieldDomain";
import { CompanyRoleImportState } from "@/customFields/companyRoleImportDomain";
import { CompanyRoleFieldsState } from "@/customFields/roleFieldsDomain";
import { CompanyRolePeriodsState } from "@/companyRoles/rolePeriodsDomain";
import { CompanySearchState } from "@/search/companySearchDomain";
import { AnnouncementDTO } from "@/announcement/announcementDomain";
import { validateFiles } from "@/fileUpload/fileUploadDomain";
import { DocumentForViewingState } from "@/document/documentApi";
import { when } from "@/utils/functions";
import { PowerOfAttorneyState } from "@/overview/powerOfAttorney/powerOfAttorneyDomain";
import { ArticlesOfAssociationState } from "@/overview/ArticlesOfAssociation/articlesOfAssociationDomain";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { fromQueryString, QueryParams } from "@/utils/queryParams";
import { ContactsState } from "@/portfolio/contacts/contactsDomain";
import { OwnershipState } from "@/overview/ownership/ownershipDomain";
import { OptionsPageState } from "@/instruments/optionsDomain";
import { CapTablePageState } from "@/shareholderRegister/capTable/capTableDomain";
import { CapTableScenarioPageState } from "@/shareholderRegister/capTable/capTableScenarioDomain";
import type { ProjectListState } from "@/dbot/projectList/projectListDomain";
import type { OpenProject } from "@/dbot/project/projectDomain";
import type { ArchiveState } from "@/archive/archiveDomain";
import { CompanyRolesState } from "@/companyRoles/companyRolesDomain.ts";
import {
  handleTelemetryEvent,
  initialTelemetryState,
  newCorrelationId,
  TelemetryState,
} from "@/telemetry/telemetryDomain";
import { gitSha } from "@/version";
import { UserSettingsState } from "@/userSettings/userSettingsDomain";
import { PortfolioReportsState } from "@/portfolio/reports/reportsDomain";
import { TemplateState } from "@/templates/templateStateDomain";

export class RootStore implements RootStoreData {
  archive?: ArchiveState;
  http: AxiosInstance;
  apiService: ApiService;
  history: History;
  userStore: UserStore;
  messageStore: AlertMessageStore;
  shareholdersStore: ShareholdersStore;
  transactionStore: TransactionStore;
  shareTypes?: ShareTypesState;
  dialogStore: DialogStore;
  portfolioStore: PortfolioStore;
  affiliateService: AffiliateService;
  webSocketStore?: WebSocketStore;
  beneficialOwnersStore: BeneficialOwnersStore;
  signDocument?: SignDocumentPageState;
  i18n: I18nState;
  userPrefs: UserPrefs;
  portfolioState?: PortfolioPageState;
  portfolioSignatures?: PortfolioSignaturesState;
  schedule?: ScheduleState;
  followUps?: FollowUpsState;
  followUpCounts?: FollowUpCounts;
  companySettings?: CompanySettingsState;
  dialogs?: DialogType[];
  route?: MatchedDCRoute;
  adminSearch?: AdminSearchPageState;
  portfolioSearch?: PortfolioSearchState;
  portfolioReports?: PortfolioReportsState;
  partnershipRegister?: PartnershipRegisterState;
  companySearch?: CompanySearchState;
  announcement?: AnnouncementDTO;
  documentForViewing?: DocumentForViewingState;
  powerOfAttorney?: PowerOfAttorneyState;
  articlesOfAssociation?: ArticlesOfAssociationState;
  contacts?: ContactsState;
  translationAdmin?: TranslationAdminState;
  entityData?: EntityDataState;
  companyFieldDetails?: CompanyFieldDetailsState;
  companyFieldEdit?: CompanyFieldEditState;
  accountFields?: AccountFieldsState;
  accountCompanyRoles?: AccountCompanyRolesState;
  dashboardSettings?: DashboardSettingsState;
  companyDashboards?: CompanyDashboardsState;
  entityDataImport?: EntityDataImportState;
  companyRoleImport?: CompanyRoleImportState;
  addCompany?: AddCompanyState;
  companyImport?: CompanyImportState;
  ownershipImport?: OwnershipImportState;
  fieldHistoryImport?: FieldHistoryImportState;
  timeouts: Timeouts = new Map();
  ownershipState?: OwnershipState;
  optionsState?: OptionsPageState;
  capTablePage?: CapTablePageState;
  capTableScenarioPage?: CapTableScenarioPageState;
  projectList?: ProjectListState;
  openProject?: OpenProject;
  companyRoles?: CompanyRolesState;
  companyRoleFields?: CompanyRoleFieldsState;
  companyRolePeriods?: CompanyRolePeriodsState;
  acceptUserTerms?: AcceptUserTermsState;
  companyIds?: CompanyIds;
  telemetry: TelemetryState;
  userSettings?: UserSettingsState;
  templates?: TemplateState;

  constructor(http: AxiosInstance, apiService: ApiService, history: History) {
    this.http = http;
    this.apiService = apiService;
    this.history = history;
    this.userStore = new UserStore(apiService, history, this.dispatch);
    this.messageStore = new AlertMessageStore();
    this.dialogStore = new DialogStore(
      this.dispatch,
      this.userStore,
      this.apiService,
      history,
      this.http,
      this.messageStore,
    );
    const downloadService = new DownloadService(this.http);
    this.transactionStore = new TransactionStore(
      apiService,
      http,
      this.userStore,
      this.messageStore,
      history,
      downloadService,
      this.dialogStore,
    );
    this.shareholdersStore = new ShareholdersStore(
      history,
      apiService,
      http,
      this.messageStore,
      this.userStore,
      this.dialogStore,
      this.transactionStore,
    );
    this.portfolioStore = new PortfolioStore(
      this.userStore,
      this.shareholdersStore,
      this.dialogStore,
      history,
      this.http,
    );
    this.affiliateService = new AffiliateService(http);
    this.beneficialOwnersStore = new BeneficialOwnersStore(
      new BeneficialOwnersApiClient(http),
      downloadService,
      this.messageStore,
      this.userStore,
      this.dispatch,
    );
    this.userPrefs = initUserPrefs();
    this.i18n = initialI18nState();
    this.telemetry = initialTelemetryState({
      correlationId: newCorrelationId(),
      sha: gitSha ?? "dev",
    });
    makeAutoObservable(
      this,
      {
        http: false,
        history: false,
        telemetry: false,
        portfolioSearch: observable.ref,
        portfolioReports: observable.ref,
        entityDataImport: observable.ref,
        companyFieldDetails: observable.ref,
        companyFieldEdit: observable.ref,
        companyRoleImport: observable.ref,
        openProject: observable.ref,
      },
      { autoBind: true },
    );
  }

  handleEvent = (event: DCEvent, _: number, context: DCEvent[]) => {
    switch (event.type) {
      case "showAcceptCompanyTermsDialog":
        this.dialogStore.openAcceptCompanyTermsDialog();
        break;
      case "showPaymentDialog":
        this.dialogStore.openSubscriptionDialog(
          this.i18n.lang,
          this.userStore,
          this.affiliateService,
        );
        break;
      case "showCreatePortfolioDialog":
        this.dialogStore.openCreatePortfolioDialog();
        break;
      case "showSendInvitationDialog":
        this.dialogStore.openSendInvitationDialog(event.data);
        break;
      case "showVerifyEmailDialog": {
        const { user } = this.userStore;
        if (user?.email && !user.isEmailVerified) {
          this.dialogStore.openVerifyEmailDialog(user.email);
        }
        break;
      }
      case "showInviteNewAdminDialog":
        this.dialogStore.openInviteNewAdminDialog({
          lang: event.lang ?? this.i18n.lang,
          companies: getCompaniesWithAdminAccessType(this.portfolioStore.allCompanies),
          selectedPortfolioId:
            event.portfolio.type === "portfolio-instance"
              ? event.portfolio.portfolio.id
              : undefined,
        });
        break;
      case "showGeneralMeetingAnnouncementDialog":
        this.dialogStore.openGeneralMeetingReleaseDialog();
        break;
      case "showEditEmailDialog":
        this.portfolioStore.openEditEmailDialog({
          id: event.externalId,
          email: event.email,
          onSuccess: event.onSuccess,
          name: event.name,
          customRoleId: event.customRoleId,
        });
        break;
      case "showRevokeAllDialog":
        this.portfolioStore.openRevokeAllDialog({
          orgNos: event.orgNos,
          externalId: event.externalId,
          name: event.name,
          onSuccess: event.onSuccess,
          portfolio: event.portfolio,
        });
        break;
      case "preview-file":
        this.dialogStore.openDialog({
          type: "preview-file",
          file: event.file,
          filename: event.filename,
          conversion: event.conversion,
        });
        break;
      case "new-meeting":
        this.dialogStore.openNewMeetingDialog(
          event.orgNo,
          event.meetingType,
          event.projectName,
          event.projectId,
        );
        break;
      case "editProjectName":
        this.dialogStore.openDialog({
          type: "editProjectName",
          name: event.name,
          save: event.save,
        });
        break;
      case "showChangePermissionDialog":
        this.dialogStore.openChangePermissionDialog(
          event.title,
          event.objectId,
          event.moduleName,
          event.onUpdate,
        );
        break;
      case "closeDialog":
        this.dialogs?.pop();
        break;
      case "subscribe-to-web-socket-messages":
        this.createWebSocketStore().subscribe(
          event.topic,
          event.onMessage,
          event.onSubscribed,
          event.headers,
        );
        break;
      case "unsubscribe-from-web-socket-messages":
        event.topics.forEach((topic) => this.createWebSocketStore().unsubscribe(topic));
        break;
      case "load-url":
        handleLoadUrlEvent(event);
        break;

      case "show-alert":
        handleShowAlertEvent(this.messageStore, event);
        break;

      case "reload":
        handleReloadEvent();
        break;

      case "http":
        handleHttpEvent(this.http, this.dispatch, event, this);
        break;

      case "update-store":
        handleUpdateStoreEvent(this, event);
        break;

      case "update-local-storage":
        localStorage.setItem(event.key, JSON.stringify(event.val));
        break;

      case "update-session-storage":
        sessionStorage.setItem(event.key, event.val);
        break;

      case "navigate":
        handleNavigateEvent(this.history, event);
        break;

      case "navigate-to":
        handleNavigateToEvent(this.history, event);
        break;

      case "log-error":
        handleLogErrorEvent(event, context);
        break;

      case "with-store":
        this.dispatch(event.events(this));
        break;

      case "order-live-general-meeting":
        this.handleOrderGeneralMeeting(event);
        break;

      case "load-project":
        if (this.userStore.user && this.userStore.companyInfo) {
          loadProject(
            this.http,
            this.history,
            this.userStore.user,
            this.userStore.companyInfo,
            event.projectId,
            AccessType.ADMIN,
            this.dispatch,
            this,
            this.userStore.companiesWithDbotAccess,
          )
            .then((ctrl) => this.dispatch(event.onSuccess(ctrl)))
            .catch((err) => this.dispatch(event.onFailure(err)));
        }
        break;

      case "show-dialog":
        if (!this.dialogs) {
          this.dialogs = [];
        }
        this.dialogs.push(event.dialog);
        break;

      case "thunk":
        handleThunkEvent(event, this.dispatch);
        break;

      case "copy-to-clipboard":
        handleCopyToClipboardEvent(event, this.dispatch);
        break;

      case "fetchTransactionLog":
        this.apiService
          .fetchTransactionLog(event.orgNo)
          .then((transactions) => event.onSuccess(transactions))
          .catch(() => this.messageStore.showError({ key: "shareholders.error.load" }));
        break;

      case "validate-files":
        validateFiles(event.files, event.fileUploadConfig)
          .then(() => this.dispatch(event.onSuccess))
          .catch((err) =>
            this.dispatch(event.onFailure(createFileTypeValidationErrorMessage(err), err)),
          );
        break;

      case "download-file":
        this.http
          .request({
            method: event.method ?? "get",
            url: event.url.startsWith("/api") ? event.url.substring("/api".length) : event.url,
            data: event.data,
            headers: { Accept: "*/*" },
            responseType: "blob",
          })
          .then((res) => {
            downloadFile(res.data, getFilenameFromHeaders(res.headers));
            when(event.onSuccess, this.dispatch);
          })
          .catch(() => {
            when(event.onFailure, this.dispatch);
          });
        break;

      case "update-company-info":
        this.userStore.updateCompanyInfo();
        break;

      case "set-timeout":
        handleSetTimeoutEvent(this.timeouts, event, this.dispatch);
        break;

      case "clear-timeout":
        handleClearTimeoutEvent(this.timeouts, event);
        break;

      case "telemetry":
        this.telemetry = handleTelemetryEvent(this.telemetry, event);
        break;
    }
  };

  dispatch = createEventDispatcher(this.handleEvent);

  private createWebSocketStore() {
    if (!this.webSocketStore) {
      this.webSocketStore = new WebSocketStore();
    }
    return this.webSocketStore;
  }

  handleOrderGeneralMeeting = (event: OrderLiveGeneralMeetingEvent) => {
    this.http
      .post<void>(
        `/project/${event.orgNo}/order-live-general-meeting?generalMeetingDate=${event.generalMeetingDate}${event.projectId ? "&projectId=" + event.projectId : ""}`,
        {},
      )
      .then(event.onSuccess)
      .catch(event.onFailure);
  };

  setRoute(route: MatchedDCRoute | undefined) {
    logDebug("RootStore::setRoute", route);
    this.route = route;
  }

  get location(): Location {
    if (this.route) {
      // Note: need to dereference `route` so that observers of `location` gets notified.
      // This is because `history` and `location` are not observed by this store, as that
      // messes up the history object. But we know that `route` changes whenever the
      // history changes, so we can safely use that as a reference.
    }
    const location = this.history.location;
    const pathname = toAppPath(location.pathname, this.companyIds);
    return pathname === location.pathname ? location : { ...location, pathname };
  }

  get locationStr(): string {
    if (this.route) {
      // See above comment. Same goes here.
    }
    return this.location.pathname + this.location.search;
  }

  get queryParams(): QueryParams {
    return fromQueryString(this.location.search);
  }
}
